import type {FlashProps} from '@primer/react'
import {createContext, useCallback, use, useMemo, useState} from 'react'
// useLocation is being used to detect page navs which don't need to be handled by partials.
import {useLocation} from '@github-ui/react-router'

type Banner = {variant: FlashProps['variant']; message: string}

interface CodeViewBannersActions {
  addBanner: (banner: Banner) => void
  addQueuedBanner: (banner: Banner) => void
}

// The banner list changes whenever a banner is added or navigation promotes the queue. It is kept in
// its own context so consumers that only need the stable adder functions don't re-render when it changes.
const CodeViewBannersDataContext = createContext<Banner[]>([])

// The adder functions never change, so they live in a separate, stable context.
const CodeViewBannersActionsContext = createContext<CodeViewBannersActions>({
  addBanner: () => undefined,
  addQueuedBanner: () => undefined,
})

export function useCodeViewBanners() {
  return use(CodeViewBannersDataContext)
}
/**
 * Gets a function to add a banner. This function never changes and is thus useful for code in useEffect().
 */
export function useCodeViewAddBanner() {
  return use(CodeViewBannersActionsContext).addBanner
}

/**
 * Gets a function to add a banner to the banner queue to make it appear on the next navigate
 * This function never changes and is thus useful for code in useEffect().
 */
export function useCodeViewAddQueuedBanner() {
  return use(CodeViewBannersActionsContext).addQueuedBanner
}

export function CodeViewBannersProvider({children}: {children: React.ReactNode}) {
  const location = useLocation()
  const [banners, setBanners] = useState<Banner[]>([])
  const [queuedBanners, setQueuedBanners] = useState<Banner[]>([])
  const addBanner = useCallback((b: Banner) => setBanners(current => [...current, b]), [])
  const addQueuedBanner = useCallback((b: Banner) => setQueuedBanners(current => [...current, b]), [])
  const actions = useMemo(() => ({addBanner, addQueuedBanner}), [addBanner, addQueuedBanner])

  // Promote queued banners on page navigation. Handled during render instead of in an effect.
  const [prevLocationKey, setPrevLocationKey] = useState(location.key)
  if (prevLocationKey !== location.key) {
    setPrevLocationKey(location.key)
    setBanners(queuedBanners)
    setQueuedBanners([])
  }
  return (
    <CodeViewBannersActionsContext value={actions}>
      <CodeViewBannersDataContext value={banners}>{children}</CodeViewBannersDataContext>
    </CodeViewBannersActionsContext>
  )
}
